/**
 * Athena feature flags — resolved server-side, per caller.
 *
 * The Athena proxy owns the flag definitions (`server.features` in its
 * config) and answers GET /api/features with the requesting user's own
 * flags, derived from the verified Okta token's email. That replaces the
 * Bonds pattern of a build-time VITE_ allowlist copy: one question, one
 * source of truth (ADR 0006 in demo-sage-athena). As everywhere in this
 * product layer, flags drive UX only — every guarded route re-checks
 * server-side. A failed lookup resolves to "no flags": fail closed.
 */
import { useEffect, useState } from "react";
import { OktaAuthClient } from "../../auth/okta";
import { athenaApiBase } from "./config";

export interface AthenaFeatures {
  conversationExport: boolean;
  athenaSearch: boolean;
}

export const fetchAthenaFeatures = async (): Promise<AthenaFeatures> => {
  const base = athenaApiBase();
  if (!base) {
    throw new Error("Athena features: VITE_ATHENA_API_URL is not configured");
  }

  const accessToken = OktaAuthClient.getAccessToken();
  if (!accessToken) {
    throw new Error("Athena features: no active access token");
  }

  const response = await fetch(`${base}/api/features`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error(`Athena features lookup failed: ${response.status}`);
  }

  const data = (await response.json()) as { features?: Partial<AthenaFeatures> };
  return { conversationExport: data.features?.conversationExport === true, athenaSearch: data.features?.athenaSearch === true };
};

// One lookup per page load, shared by every consumer and deduped like
// fetchSageAccess does. A rejected lookup is dropped so a later mount can
// retry rather than caching a failure forever.
let featuresPromise: Promise<AthenaFeatures> | null = null;

export const loadAthenaFeatures = (): Promise<AthenaFeatures> => {
  if (!featuresPromise) {
    featuresPromise = fetchAthenaFeatures().catch(error => {
      featuresPromise = null;
      throw error;
    });
  }
  return featuresPromise;
};

/** Fail-closed convenience for UI code: a broken lookup means no flags. */
export const conversationExportEnabled = async (): Promise<boolean> => {
  try {
    return (await loadAthenaFeatures()).conversationExport;
  } catch {
    return false;
  }
};

export const athenaSearchEnabled = async (): Promise<boolean> => {
  try {
    return (await loadAthenaFeatures()).athenaSearch;
  } catch {
    return false;
  }
};

export const useAthenaSearchFlag = (): "pending" | "on" | "off" => {
  const [state, setState] = useState<"pending" | "on" | "off">("pending");
  useEffect(() => {
    let active = true;
    athenaSearchEnabled().then(ok => {
      if (active) setState(ok ? "on" : "off");
    });
    return () => {
      active = false;
    };
  }, []);
  return state;
};

export const _resetAthenaFeaturesCacheForTesting = (): void => {
  featuresPromise = null;
};
